import { Component, ViewChild } from '@angular/core';
import { BsModalRef, ModalDirective } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { HttpService } from 'src/app/services/http.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpEventType} from '@angular/common/http';
import { map } from 'rxjs/operators';
import { BlastData } from 'src/app/bo/blastModel/blastData';

@Component({
  selector: 'app-import-modal',
  templateUrl: './import-modal.component.html',
  styleUrls: ['./import-modal.component.scss'],
  standalone: false
})
export class ImportModalComponent {
    @ViewChild('modalConfirmUpdate') modalConfirmUpdate: ModalDirective;
    selectedFile: File | null = null;
    uploadUrl: string;
    barValue = '0%';
    allowCsvOrXlsxImport: boolean;
    allowStrFileImport: boolean;
    blastData: BlastData;
    swapXY = true;
    isUploading = false;
    isConfirming = false;

    constructor(
        private readonly http: HttpService,
        private readonly bsModalRef: BsModalRef,
        private readonly translate: TranslateService,
        private readonly fonctionsService: FonctionsService,
    ) { }

    openFileSelector() {
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        fileInput.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (this.isFileAllowed(file)) {
                this.selectedFile = file;
            } else {
                this.showFileError();
            }
        }
    }

    dropped(files: NgxFileDropEntry[]) {
        if (files.length > 0 && files[0].fileEntry.isFile) {
            const fileEntry = files[0].fileEntry as FileSystemFileEntry;
            fileEntry.file((file: File) => {
                if (this.isFileAllowed(file)) {
                    this.selectedFile = file;
                } else {
                    this.showFileError();
                }
            });
        }
    }

    getAcceptedFileTypes(): string {
        if (this.allowCsvOrXlsxImport) {
            return '.csv, .xlsx';
        } else if (this.allowStrFileImport) {
            return '.str';
        }
        return '';
    }

    isFileAllowed(file: File): boolean {
        const allowedExtensions: string[] = this.getAcceptedFileTypes().split(',').map(ext => ext.trim().substring(1));
        const fileExtension = file.name.split('.').pop().toLowerCase();
        return allowedExtensions.includes(fileExtension);
    }

    showFileError() {
        this.translate.get('GEODATA.UNAUTHORIZED_FORMAT').subscribe(trad => {
            this.fonctionsService.launch_toast(false, false, trad);
        });
    }

    removeFile() {
        this.selectedFile = null;
    }

    uploadFile() {
        if (!this.selectedFile || this.isUploading) {
            return;
        }
        this.isUploading = true;
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        this.fonctionsService.loading_screen(true);
        this.http.postFiles(this.uploadUrl+"?swapXY="+this.swapXY, formData)
            .pipe(map(event => {
                switch (event.type) {
                    case HttpEventType.UploadProgress:
                        this.barValue = Math.round((100 / (event.total || 1) * event.loaded)) + '%';
                        break;
                    case HttpEventType.Response:
                        this.isUploading = false;
                        const body = event.body as BlastData | { statusCode: number, message: string};
                        const status = body !== null && 'statusCode' in body ? body.statusCode : event.status;
                        const message = body !== null && 'message' in body ? body.message : "";
                        if (status == 200){
                            if (this.allowStrFileImport){
                                if (body !== null && 'blastName' in body && 'blastId' in body) {
                                    this.blastData = {
                                        blastName: body.blastName,
                                        blastId: body.blastId
                                    };
                                    this.fonctionsService.loading_screen(false);
                                    this.showConfirmationDialog();
                                }
                                else{
                                this.fonctionsService.loading_screen(false);
                                this.fonctionsService.launch_toast(true);
                                this.bsModalRef.hide();
                                }
                            }else{
                                setTimeout(() => {
                                    this.barValue = '0%';
                                }, 500);
                                this.fonctionsService.loading_screen(false);
                                this.fonctionsService.launch_toast(true);
                                this.bsModalRef.hide();
                            }
                            break;
                        } else if (status == 400){
                            this.fonctionsService.launch_toast(false, false, message);
                            this.fonctionsService.loading_screen(false);
                        } else if (status == 404){
                            this.fonctionsService.launch_toast(false, false, message);
                            this.fonctionsService.loading_screen(false);
                        }
                }
            }))
            .subscribe({
                next: () => {},
                error: (error) => {
                    this.isUploading = false;
                    const errorMessage = error?.error?.message || error?.message || '';
                    this.fonctionsService.launch_toast(false, false, errorMessage);
                    this.fonctionsService.loading_screen(false);
                },
                complete: () => {
                    this.isUploading = false;
                }
            });
    }

    showConfirmationDialog() {
        this.modalConfirmUpdate.show();
    }

    confirmUpdate() {
        if (this.isConfirming) {
            return;
        }
        this.isConfirming = true;
        this.update().subscribe({
            next: () => {
                this.modalConfirmUpdate.hide();
                this.bsModalRef.hide();
                this.blastData = null;
                this.isConfirming = false;
            },
            error: () => {
                this.fonctionsService.launch_toast(false);
                this.isConfirming = false;
            }
        });
    }

    hideModal() {
        this.bsModalRef.hide();
    }

    declineUpdate() {
        this.modalConfirmUpdate.hide();
        this.bsModalRef.hide();
    }

    private update() {
        if (this.allowStrFileImport){
            const url = `Blasts/${this.blastData.blastId}/UpdateBlastName?blastName=${encodeURIComponent(this.blastData.blastName)}`;
            return this.http.put(url, null).pipe(
                map((res: number) => {
                    return res;
                })
            );
        }
    }
}
